
import { 
    Document, 
    Packer, 
    Paragraph, 
    TextRun, 
    HeadingLevel, 
    AlignmentType,
    UnderlineType,
    BorderStyle,
    WidthType,
    TableCell,
    TableRow,
    Table,
    LineRuleType
} from 'docx';
import FileSaver from 'file-saver';

export function useDocxExport() {
    const exportToDocx = async (htmlContent: string, fileName = 'documento') => {
        try {
            // Converter o HTML em texto simples
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = htmlContent;
            
            // Extrair o conteúdo
            const paragraphs = [];
            
            // Definindo estilos padrão para os diferentes elementos
            const STYLES = {
                normal: {
                    font: 'Arial',
                    size: 24, // 12pt
                    spacing: {
                        line: 360, // 1.5 linha
                        before: 0,
                        after: 240, // 12pt depois
                    },
                    alignment: AlignmentType.JUSTIFIED
                },
                heading1: {
                    font: 'Arial',
                    size: 32, // 16pt
                    bold: true,
                    spacing: {
                        before: 240, // 12pt antes
                        after: 120, // 6pt depois
                    },
                    alignment: AlignmentType.CENTER
                },
                heading2: {
                    font: 'Arial',
                    size: 28, // 14pt
                    bold: true,
                    spacing: {
                        before: 240, // 12pt antes
                        after: 120, // 6pt depois
                    },
                    alignment: AlignmentType.LEFT
                }
            };
            
            // Processar cada elemento para criar parágrafos no DOCX
            const processNode = (node: Node) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    if (node.textContent && node.textContent.trim() !== '') {
                        paragraphs.push(new Paragraph({
                            children: [
                                new TextRun({
                                    text: node.textContent.trim(),
                                    size: STYLES.normal.size,
                                    font: STYLES.normal.font
                                })
                            ],
                            spacing: STYLES.normal.spacing,
                            alignment: STYLES.normal.alignment
                        }));
                    }
                } else if (node.nodeType === Node.ELEMENT_NODE) {
                    const element = node as Element;
                    const tagName = element.tagName.toLowerCase();
                    
                    if (tagName === 'h1') {
                        paragraphs.push(new Paragraph({
                            children: [
                                new TextRun({
                                    text: element.textContent || '',
                                    size: STYLES.heading1.size,
                                    bold: STYLES.heading1.bold,
                                    font: STYLES.heading1.font,
                                    allCaps: true
                                })
                            ],
                            heading: HeadingLevel.HEADING_1,
                            alignment: STYLES.heading1.alignment,
                            spacing: STYLES.heading1.spacing,
                            border: {
                                bottom: {
                                    color: "auto",
                                    style: BorderStyle.SINGLE,
                                    size: 6
                                }
                            }
                        }));
                    } else if (tagName === 'h2') {
                        paragraphs.push(new Paragraph({
                            children: [
                                new TextRun({
                                    text: element.textContent || '',
                                    size: STYLES.heading2.size,
                                    bold: STYLES.heading2.bold,
                                    font: STYLES.heading2.font
                                })
                            ],
                            heading: HeadingLevel.HEADING_2,
                            alignment: STYLES.heading2.alignment,
                            spacing: STYLES.heading2.spacing
                        }));
                    } else if (tagName === 'p') {
                        // Verifica se é um parágrafo com formatação especial (como assinatura)
                        const isSignature = element.textContent?.includes('Atenciosamente') || 
                                           element.textContent?.includes('Nestes termos');
                        const isDate = element.textContent?.includes('de ') && 
                                      (element.textContent?.includes('janeiro') || 
                                       element.textContent?.includes('fevereiro') || 
                                       element.textContent?.includes('março'));
                        
                        paragraphs.push(new Paragraph({
                            children: [
                                new TextRun({
                                    text: element.textContent || '',
                                    size: STYLES.normal.size,
                                    font: STYLES.normal.font,
                                    italics: isSignature
                                })
                            ],
                            alignment: isSignature || isDate ? AlignmentType.RIGHT : STYLES.normal.alignment,
                            spacing: {
                                ...STYLES.normal.spacing,
                                before: isSignature ? 480 : STYLES.normal.spacing.before // Adiciona espaço antes de assinaturas
                            },
                            indent: {
                                firstLine: isSignature || isDate ? 0 : 720 // 1.25cm para recuo da primeira linha em parágrafos normais
                            }
                        }));
                    } else if (tagName === 'strong' || tagName === 'b') {
                        paragraphs.push(new Paragraph({
                            children: [
                                new TextRun({
                                    text: element.textContent || '',
                                    bold: true,
                                    size: STYLES.normal.size,
                                    font: STYLES.normal.font
                                })
                            ],
                            spacing: STYLES.normal.spacing,
                            alignment: STYLES.normal.alignment
                        }));
                    } else if (tagName === 'em' || tagName === 'i') {
                        paragraphs.push(new Paragraph({
                            children: [
                                new TextRun({
                                    text: element.textContent || '',
                                    italics: true,
                                    size: STYLES.normal.size,
                                    font: STYLES.normal.font
                                })
                            ],
                            spacing: STYLES.normal.spacing,
                            alignment: STYLES.normal.alignment
                        }));
                    } else if (tagName === 'u') {
                        paragraphs.push(new Paragraph({
                            children: [
                                new TextRun({
                                    text: element.textContent || '',
                                    underline: {
                                        type: UnderlineType.SINGLE
                                    },
                                    size: STYLES.normal.size,
                                    font: STYLES.normal.font
                                })
                            ],
                            spacing: STYLES.normal.spacing,
                            alignment: STYLES.normal.alignment
                        }));
                    } else if (tagName === 'br') {
                        // Adicionar um parágrafo vazio para quebra de linha
                        paragraphs.push(new Paragraph({
                            spacing: {
                                before: 120,
                                after: 120
                            }
                        }));
                    } else if (tagName === 'table') {
                        // Processar tabelas
                        const rows: TableRow[] = [];
                        const tableElement = element as HTMLTableElement;
                        
                        // Processar linhas da tabela
                        Array.from(tableElement.rows).forEach(htmlRow => {
                            const tableCells: TableCell[] = [];
                            
                            // Processar células de cada linha
                            Array.from(htmlRow.cells).forEach(htmlCell => {
                                tableCells.push(
                                    new TableCell({
                                        children: [
                                            new Paragraph({
                                                children: [
                                                    new TextRun({
                                                        text: htmlCell.textContent || '',
                                                        size: STYLES.normal.size,
                                                        font: STYLES.normal.font
                                                    })
                                                ],
                                                alignment: AlignmentType.CENTER
                                            })
                                        ],
                                        margins: {
                                            top: 120,
                                            bottom: 120,
                                            left: 120,
                                            right: 120
                                        }
                                    })
                                );
                            });
                            
                            rows.push(new TableRow({ children: tableCells }));
                        });
                        
                        const table = new Table({
                            rows,
                            width: {
                                size: 100,
                                type: WidthType.PERCENTAGE
                            },
                            borders: {
                                insideHorizontal: {
                                    style: BorderStyle.SINGLE,
                                    size: 1,
                                    color: "auto"
                                },
                                insideVertical: {
                                    style: BorderStyle.SINGLE,
                                    size: 1,
                                    color: "auto"
                                },
                                top: {
                                    style: BorderStyle.SINGLE,
                                    size: 1,
                                    color: "auto"
                                },
                                bottom: {
                                    style: BorderStyle.SINGLE,
                                    size: 1,
                                    color: "auto"
                                },
                                left: {
                                    style: BorderStyle.SINGLE,
                                    size: 1,
                                    color: "auto"
                                },
                                right: {
                                    style: BorderStyle.SINGLE,
                                    size: 1,
                                    color: "auto"
                                }
                            }
                        });
                        
                        paragraphs.push(table);
                    } else if (tagName === 'ul' || tagName === 'ol') {
                        // Processar listas
                        Array.from(element.children).forEach((item, index) => {
                            if (item.tagName.toLowerCase() === 'li') {
                                const prefix = tagName === 'ol' ? `${index + 1}. ` : '• ';
                                paragraphs.push(new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: prefix + (item.textContent || ''),
                                            size: STYLES.normal.size,
                                            font: STYLES.normal.font
                                        })
                                    ],
                                    spacing: {
                                        ...STYLES.normal.spacing,
                                        before: 120,
                                        after: 120
                                    },
                                    indent: {
                                        left: 720 // 1.25cm para recuo à esquerda em itens de lista
                                    }
                                }));
                            }
                        });
                    } else {
                        // Para outros elementos, processamos seus filhos
                        element.childNodes.forEach(processNode);
                    }
                }
            };
            
            // Iniciar o processamento
            tempDiv.childNodes.forEach(processNode);
            
            // Criar o documento com margens padrão ABNT
            const doc = new Document({
                sections: [
                    {
                        properties: {
                            page: {
                                margin: {
                                    top: 567, // 2.54cm = 1 polegada
                                    right: 709, // 3.175cm = 1.25 polegadas
                                    bottom: 567, // 2.54cm = 1 polegada
                                    left: 709, // 3.175cm = 1.25 polegadas
                                },
                                size: {
                                    width: 11906, // Largura A4 em dxa (8.5 pol)
                                    height: 16838, // Altura A4 em dxa (11 pol)
                                },
                            },
                        },
                        children: paragraphs
                    }
                ]
            });
            
            // Gerar o arquivo DOCX como Blob (compatível com navegador)
            const blob = await Packer.toBlob(doc);
            
            // Salvar o arquivo
            FileSaver.saveAs(blob, `${fileName}.docx`);
        } catch (error) {
            console.error('Erro ao exportar para DOCX:', error);
        }
    };

    return { exportToDocx };
}
